import classNames from 'classnames'
import { Icon } from 'components/common/Icon'
import { useRouter } from 'next/router'
import { PropsWithChildren, useEffect, useState } from 'react'
import classes from '../../collapsable.module.sass'

type AccordionProps = {
  title: React.ReactNode
  openTitle?: React.ReactNode
  titleClassName?: string
  children: React.ReactNode
  open?: boolean
  icon?: string
  iconClassName?: string
  iconCustom?: boolean
  iconFilled?: boolean
  iconSize?: number
  onChange?: (open: boolean) => void
  className?: string
  type?: 'faq' | 'featureCard'
}

export const Collapsable = ({
  title,
  openTitle = title,
  titleClassName,
  children,
  open = false,
  icon = 'arrow-chevron-down',
  iconSize = 24,
  iconCustom,
  iconFilled,
  iconClassName,
  onChange,
  className,
  type,
}: AccordionProps) => {
  const [isOpen, setOpen] = useState<boolean>(open)
  const router = useRouter()

  useEffect(() => {
    const handleRouteChangeStart = () => {
      setOpen(open)
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [])

  const transition = `transition-transform ${
    isOpen ? 'rotate-180 translate-y-[2px] md:translate-y-[3px]' : 'rotate-0'
  }`

  if (type === 'faq') {
    return (
      <div className={`${className}`}>
        <div
          className={'flex cursor-pointer items-center justify-between'}
          onClick={() => {
            setOpen(!isOpen)
            onChange?.(!isOpen)
          }}
        >
          {title}

          <div>
            <Icon
              name={isOpen ? 'actions-minus' : 'actions-plus'}
              size={iconSize}
              filled={iconFilled}
              custom={iconCustom}
              transition={transition}
              className={` always-visible  text-cool-900 ${iconClassName}`}
            />
          </div>
        </div>

        <CollapsableContent isOpen={isOpen}>{children}</CollapsableContent>
      </div>
    )
  }

  return (
    <div
      className={classNames({
        'mt-4 flex flex-col-reverse': type === 'featureCard',
      })}
    >
      <div
        className={`flex cursor-pointer items-center justify-between ${titleClassName}`}
        onClick={() => {
          setOpen(!isOpen)
          onChange?.(!isOpen)
        }}
      >
        <span className='mr-2 flex items-center'>
          {isOpen ? openTitle : title}
        </span>

        <div>
          <Icon
            name={icon}
            size={iconSize}
            filled={iconFilled}
            custom={iconCustom}
            transition={transition}
            className={`text-cool-900 ${iconClassName}`}
          />
        </div>
      </div>

      <CollapsableContent isOpen={isOpen}>{children}</CollapsableContent>
    </div>
  )
}

type CollapsableContentProps = {
  isOpen: boolean
} & PropsWithChildren

function CollapsableContent({ isOpen, children }: CollapsableContentProps) {
  return (
    <div
      className={classNames(
        'overflow-hidden',
        classes['collapsable'],
        isOpen && classes['collapsable_open']
      )}
    >
      <div className='flex'>{children}</div>
    </div>
  )
}
